import { makeAutoObservable, runInAction } from "mobx";

import { StreamTextSmoother } from "./StreamTextSmoother";

export class StreamingChatStore {
  private _streamingText: string = "";
  private _streamingThoughts: string = "";
  private _currentAction: string = "";
  private _isStreaming: boolean = false;
  private _streamingExchangePrompt: string = "";

  /** Prompt + partial answer captured when the user cancels an in-flight streaming exchange. */
  private _cancelledExchangePreview: { prompt: string; partialAnswer: string } | null = null;

  /** Maps exchangeId → thoughts for the current session (ephemeral, cleared on page refresh) */
  private _thoughtsByExchangeId: Map<string, string> = new Map();

  /** Non-observable callback registered by ChatService to abort the in-flight HTTP request. */
  private _stopCallback: (() => void) | null = null;

  private readonly _textSmoother: StreamTextSmoother;
  private readonly _thoughtSmoother: StreamTextSmoother;

  constructor() {
    makeAutoObservable<StreamingChatStore, "_textSmoother" | "_thoughtSmoother" | "_stopCallback">(this, {
      _textSmoother: false,
      _thoughtSmoother: false,
      _stopCallback: false,
    });

    this._textSmoother = new StreamTextSmoother(text => {
      runInAction(() => {
        this._streamingText = text;
      });
    });

    this._thoughtSmoother = new StreamTextSmoother(text => {
      runInAction(() => {
        this._streamingThoughts = text;
      });
    });
  }

  public get streamingText(): string {
    return this._streamingText;
  }

  public get streamingThoughts(): string {
    return this._streamingThoughts;
  }

  public get currentAction(): string {
    return this._currentAction;
  }

  public get isStreaming(): boolean {
    return this._isStreaming;
  }

  public get streamingExchangePrompt(): string {
    return this._streamingExchangePrompt;
  }

  /** Prompt + partial answer captured when the user cancels an in-flight streaming exchange. */
  public get cancelledExchangePreview(): { prompt: string; partialAnswer: string } | null {
    return this._cancelledExchangePreview;
  }

  public startStreaming(prompt: string): void {
    this._textSmoother.reset();
    this._thoughtSmoother.reset();
    this._isStreaming = true;
    this._streamingExchangePrompt = prompt;
    this._streamingText = "";
    this._streamingThoughts = "";
    this._currentAction = "";
    this._cancelledExchangePreview = null; // clear any previous cancelled-exchange state on new stream
    this._stopCallback = null;
  }

  /** Registers the callback that aborts the in-flight HTTP request. Called by ChatService. */
  public registerStopCallback(callback: () => void): void {
    this._stopCallback = callback;
  }

  public appendText(chunk: string): void {
    this._textSmoother.push(chunk);
  }

  public appendThought(chunk: string): void {
    this._thoughtSmoother.push(chunk);
  }

  public setAction(action: string): void {
    this._currentAction = action;
  }

  public finalizeStreaming(exchangeId?: string): void {
    this._textSmoother.flush();
    this._thoughtSmoother.flush();

    if (exchangeId && this._streamingThoughts.length > 0) {
      this._thoughtsByExchangeId.set(exchangeId, this._streamingThoughts);
    }

    this._isStreaming = false;
    this._streamingText = "";
    this._streamingThoughts = "";
    this._currentAction = "";
    this._streamingExchangePrompt = "";
    this._stopCallback = null;
  }

  /**
   * Stops the in-flight stream by invoking the registered abort callback.
   * The cancelled question will be restored to the input field by ChatService on AbortError.
   */
  public stopStreaming(): void {
    this._textSmoother.flush();
    this._thoughtSmoother.flush();

    // Capture the cancelled prompt and partial answer before clearing streaming state,
    // so the UI can show that the exchange was stopped.
    this._cancelledExchangePreview = {
      prompt: this._streamingExchangePrompt,
      partialAnswer: this._streamingText,
    };

    this._isStreaming = false;
    this._streamingText = "";
    this._streamingThoughts = "";
    this._currentAction = "";
    this._streamingExchangePrompt = "";

    this._stopCallback?.();
    this._stopCallback = null;
  }

  public reset(): void {
    this._textSmoother.reset();
    this._thoughtSmoother.reset();
    this._isStreaming = false;
    this._streamingText = "";
    this._streamingThoughts = "";
    this._currentAction = "";
    this._streamingExchangePrompt = "";
    this._cancelledExchangePreview = null; // clear any previous cancelled-exchange state on reset
    this._stopCallback = null;
  }

  public getThoughtsForExchange(exchangeId: string): string | undefined {
    return this._thoughtsByExchangeId.get(exchangeId);
  }

  public get thoughtsByExchangeId(): Map<string, string> {
    return this._thoughtsByExchangeId;
  }
}
